import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { appDb, SyncMetadata, SyncOperation, SyncQueueItem } from '@core/db/app-db';
import { ConnectivityService } from '@core/services/connectivity.service';
import { JwtService } from '@core/services/jwt.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { shouldUseOfflineFallback, withSyncMetadata } from '@core/services/offline.utils';

type BatchOperation = SyncQueueItem & { id: number };

type BatchResult = {
  queueId: number;
  success: boolean;
  entity: string;
  operation: SyncOperation;
  data?: Record<string, unknown> | null;
  error?: string;
};

@Injectable({ providedIn: 'root' })
export class SyncService {
  private readonly jwtService = inject(JwtService);
  private readonly connectivityService = inject(ConnectivityService);
  private readonly tenantSessionService = inject(TenantSessionService);

  readonly isSyncing = signal(false);
  readonly pendingCount = signal(0);

  constructor() {
    void this.refreshPendingCount().catch((error) => {
      console.warn('No fue posible leer la cola offline:', error);
    });

    this.connectivityService.online$.subscribe({
      next: (isOnline) => {
        if (!isOnline) {
          return;
        }

        void this.syncPending().catch((error) => {
          console.warn('No fue posible sincronizar la cola offline:', error);
        });
      },
      error: (error) => {
        console.warn('No fue posible observar la conexión para sincronización:', error);
      },
    });
  }

  async enqueue(
    entidad: string,
    operacion: SyncOperation,
    payload: Record<string, unknown>,
  ): Promise<void> {
    await appDb.syncQueue.add({
      entidad,
      operacion,
      payload,
      intentos: 0,
      fecha: new Date().toISOString(),
    });

    await this.refreshPendingCount();
  }

  async syncPending(): Promise<void> {
    if (this.isSyncing() || !this.connectivityService.isOnline()) {
      return;
    }

    const queueItems = await appDb.syncQueue
      .orderBy('fecha')
      .filter((item) => item.intentos < 3)
      .toArray() as BatchOperation[];

    const validQueueItems = await this.discardInvalidQueueItems(queueItems);

    if (validQueueItems.length === 0) {
      await this.refreshPendingCount();
      return;
    }

    let authToken = this.tenantSessionService.getAuthToken();
    const tenantId = this.tenantSessionService.activeTenantId;
    const firebaseIdToken = this.tenantSessionService.getFirebaseIdToken();

    if (!authToken) {
      return;
    }

    this.isSyncing.set(true);

    try {
      let response: Response;
      try {
        if (this.jwtService.isTokenExpired(authToken)) {
          const renewal = await firstValueFrom(this.jwtService.refreshToken(authToken));
          if (!renewal.data) return;
          authToken = renewal.data;
          this.jwtService.setAuthToken(authToken);
        }
        if (this.tenantSessionService.activeTenantId !== tenantId) return;
        response = await fetch(`${environment.api_rest_url}/sync/batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
            ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
            ...(firebaseIdToken ? { 'x-firebase-id-token': firebaseIdToken } : {}),
          },
          body: JSON.stringify({
            operations: validQueueItems.map((item) => ({
              queueId: item.id,
              entity: item.entidad,
              operation: item.operacion,
              payload: item.payload,
            })),
          }),
        });
        this.connectivityService.markOnline();
      } catch (error) {
        if (shouldUseOfflineFallback(error)) {
          this.connectivityService.markNetworkFailure();
          return;
        }

        throw error;
      }

      if (!response.ok) {
        await Promise.all(
          validQueueItems.map((item) =>
            appDb.syncQueue.update(item.id, { intentos: item.intentos + 1 }),
          ),
        );
        return;
      }

      const body = await response.json() as { data?: { results?: BatchResult[] } };
      const results = body.data?.results ?? [];

      for (const result of results) {
        const currentItem = validQueueItems.find((item) => item.id === result.queueId);
        if (!currentItem) {
          continue;
        }

        if (result.success) {
          await this.applySyncedResult(currentItem, result);
          await appDb.syncQueue.delete(currentItem.id);
          continue;
        }

        await appDb.syncQueue.update(currentItem.id, {
          intentos: currentItem.intentos + 1,
        });
      }
    } finally {
      this.isSyncing.set(false);
      await this.refreshPendingCount();
    }
  }

  private async discardInvalidQueueItems(
    queueItems: BatchOperation[],
  ): Promise<BatchOperation[]> {
    const validItems: BatchOperation[] = [];

    for (const item of queueItems) {
      if (this.isInvalidQueueItem(item)) {
        await appDb.syncQueue.delete(item.id);
        continue;
      }

      validItems.push(item);
    }

    return validItems;
  }

  private isInvalidQueueItem(item: BatchOperation): boolean {
    if (item.operacion === 'create') {
      return false;
    }

    if (!['brandBusiness', 'categoryBusiness'].includes(item.entidad)) {
      return false;
    }

    const normalizedId = Number(item.payload['id'] ?? 0);
    return !Number.isInteger(normalizedId) || normalizedId <= 0;
  }

  private async applySyncedResult(
    item: BatchOperation,
    result: BatchResult,
  ): Promise<void> {
    const remoteRecord = result.data ?? null;
    const localId = Number(item.payload['id'] ?? 0);

    switch (result.entity) {
      case 'product':
        if (item.operacion === 'delete') {
          await appDb.products.delete(localId);
          return;
        }
        if (localId < 0) {
          await appDb.products.delete(localId);
        }
        if (remoteRecord) {
          await appDb.products.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'supplier':
        if (item.operacion === 'delete') {
          await appDb.suppliers.delete(localId);
          return;
        }
        if (localId < 0) {
          await appDb.suppliers.delete(localId);
        }
        if (remoteRecord) {
          await appDb.suppliers.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'sale':
        if (localId < 0) {
          await appDb.sales.delete(localId);
        }
        if (remoteRecord) {
          await appDb.sales.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'cashSession':
        if (remoteRecord) {
          await appDb.cashSessions.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'cashMovement':
        if (localId < 0) {
          await appDb.cashMovements.delete(localId);
        }
        if (remoteRecord) {
          await appDb.cashMovements.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'inventoryMovement':
        if (localId < 0) {
          await appDb.inventoryMovements.delete(localId);
        }
        if (remoteRecord) {
          await appDb.inventoryMovements.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'categoryBusiness':
        if (item.operacion === 'delete') {
          await appDb.categoryBusinesses.delete(localId);
          return;
        }
        if (localId < 0) {
          await appDb.categoryBusinesses.delete(localId);
        }
        if (remoteRecord) {
          await appDb.categoryBusinesses.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      case 'brandBusiness':
        if (item.operacion === 'delete') {
          await appDb.brandBusinesses.delete(localId);
          return;
        }
        if (localId < 0) {
          await appDb.brandBusinesses.delete(localId);
        }
        if (remoteRecord) {
          await appDb.brandBusinesses.put(withSyncMetadata(remoteRecord as any, { synchronized: true }) as any);
        }
        return;
      default:
        return;
    }
  }

  private async refreshPendingCount(): Promise<void> {
    const count = await appDb.syncQueue.count();
    this.pendingCount.set(count);
  }
}
